// Gantt công việc: nhóm theo dự án, thanh = bắt đầu → hạn chót, vạch "Hôm nay", phần quá hạn / xong muộn tô riêng.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Segmented, Tooltip } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { ChevronDown, ChevronRight, LocateFixed } from 'lucide-react';
import { memberName, projectOf, type WorkTask } from '../data';
import { STATUS_META, isClosed, isOverdue } from '../flow';
import { Avatar, StatusTag, fmtDate } from '../ui';

type Zoom = 'day' | 'week' | 'month';
const DAY_WIDTH: Record<Zoom, number> = { day: 34, week: 14, month: 5 };
const ROW = 40;
const LEFT = 340;
const WEEKDAY = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
const MAX_TASKS = 300;

const startOf = (t: WorkTask) => dayjs(t.startAt ?? t.createdAt);
/** Ngày kết thúc dự kiến của thanh: hạn chót, nếu không có thì ngày xong / hôm nay */
const planEnd = (t: WorkTask) => dayjs(t.dueAt ?? t.doneAt ?? t.submittedAt ?? dayjs());

type Row = { kind: 'group'; id: string; name: string; count: number; start: Dayjs; end: Dayjs } | { kind: 'task'; t: WorkTask };

export function GanttView({ tasks, onOpen }: { tasks: WorkTask[]; onOpen: (id: string) => void }) {
  const [zoom, setZoom] = useState<Zoom>('day');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const today = dayjs().startOf('day');
  const list = tasks.slice(0, MAX_TASKS);

  // Khoảng hiển thị: từ việc sớm nhất đến việc muộn nhất, luôn chứa hôm nay, chừa lề 3 ngày
  const [from, days] = useMemo(() => {
    let min = today.subtract(7, 'day');
    let max = today.add(14, 'day');
    list.forEach(t => {
      if (startOf(t).isBefore(min)) min = startOf(t);
      const e = planEnd(t);
      if (e.isAfter(max)) max = e;
    });
    const start = min.subtract(3, 'day').startOf(zoom === 'month' ? 'month' : 'week');
    return [start, max.add(7, 'day').diff(start, 'day') + 1] as const;
  }, [list, today, zoom]); // eslint-disable-line react-hooks/exhaustive-deps

  const dw = DAY_WIDTH[zoom];
  const x = (d: Dayjs) => d.startOf('day').diff(from, 'day') * dw;
  const width = days * dw;

  const rows: Row[] = useMemo(() => {
    const groups = new Map<string, WorkTask[]>();
    list.forEach(t => groups.set(t.projectId, [...(groups.get(t.projectId) ?? []), t]));
    const out: Row[] = [];
    Array.from(groups.entries())
      .sort((a, b) => (projectOf(a[0])?.name ?? '').localeCompare(projectOf(b[0])?.name ?? ''))
      .forEach(([pid, ts]) => {
        const sorted = [...ts].sort((a, b) => startOf(a).diff(startOf(b)));
        out.push({
          kind: 'group', id: pid, name: projectOf(pid)?.name ?? pid, count: ts.length,
          start: sorted.reduce((m, t) => (startOf(t).isBefore(m) ? startOf(t) : m), startOf(sorted[0])),
          end: sorted.reduce((m, t) => (planEnd(t).isAfter(m) ? planEnd(t) : m), planEnd(sorted[0])),
        });
        if (!collapsed.has(pid)) sorted.forEach(t => out.push({ kind: 'task', t }));
      });
    return out;
  }, [list, collapsed]);

  // Thang thời gian: hàng trên = tháng, hàng dưới = ngày (zoom ngày) hoặc tuần
  const months = useMemo(() => {
    const out: { label: string; left: number; w: number }[] = [];
    let cur = from.startOf('month');
    const end = from.add(days, 'day');
    while (cur.isBefore(end)) {
      const s = cur.isBefore(from) ? from : cur;
      const e = cur.endOf('month').isAfter(end) ? end : cur.endOf('month').add(1, 'day').startOf('day');
      out.push({ label: `Tháng ${cur.month() + 1}/${cur.year()}`, left: x(s), w: e.diff(s, 'day') * dw });
      cur = cur.add(1, 'month');
    }
    return out;
  }, [from, days, dw]); // eslint-disable-line react-hooks/exhaustive-deps

  const ticks = useMemo(() => {
    const out: { key: string; label: React.ReactNode; left: number; w: number; weekend?: boolean; isToday?: boolean }[] = [];
    if (zoom === 'month') return out;
    const step = zoom === 'day' ? 1 : 7;
    for (let i = 0; i < days; i += step) {
      const d = from.add(i, 'day');
      out.push({
        key: d.format('YYYYMMDD'),
        left: i * dw,
        w: step * dw,
        weekend: zoom === 'day' && (d.day() === 0 || d.day() === 6),
        isToday: zoom === 'day' && d.isSame(today, 'day'),
        label: zoom === 'day'
          ? <><div className="text-[10px] opacity-70">{WEEKDAY[d.day()]}</div><div>{d.date()}</div></>
          : <>{d.format('DD/MM')}</>,
      });
    }
    return out;
  }, [from, days, dw, zoom]); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollToToday = () => {
    const el = scrollRef.current;
    if (el) el.scrollLeft = Math.max(0, x(today) - (el.clientWidth - LEFT) / 3);
  };
  const fromKey = from.valueOf();
  useEffect(scrollToToday, [zoom, fromKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id: string) => setCollapsed(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const bar = (t: WorkTask) => {
    const s = startOf(t);
    const due = t.dueAt ? dayjs(t.dueAt) : null;
    const finished = t.doneAt ? dayjs(t.doneAt) : t.submittedAt ? dayjs(t.submittedAt) : null;
    const end = due ?? finished ?? today;
    const left = x(s);
    const w = Math.max(dw, x(end) - left + dw);
    const color = STATUS_META[t.status].color;
    // phần kéo dài sau hạn: đỏ nếu đang quá hạn, vàng nếu đã xong nhưng muộn
    let tail: { left: number; w: number; cls: string } | null = null;
    if (due && isOverdue(t)) tail = { left: x(due) + dw, w: x(today) - x(due), cls: 'bg-red-400/80 bg-[repeating-linear-gradient(45deg,transparent,transparent_4px,rgba(255,255,255,.35)_4px,rgba(255,255,255,.35)_8px)]' };
    else if (due && finished && finished.isAfter(due, 'day')) tail = { left: x(due) + dw, w: x(finished) - x(due), cls: 'bg-amber-400' };
    const label = `${t.code} ${t.name}`;
    const tip = (
      <div className="text-xs leading-5">
        <div className="font-semibold">{label}</div>
        <div>Người làm: {memberName(t.assigneeId)}</div>
        <div>{fmtDate(t.startAt ?? t.createdAt)} → {t.dueAt ? fmtDate(t.dueAt) : 'không hạn'}</div>
        {finished && <div>{t.status === 'review' ? 'Báo xong' : 'Hoàn thành'}: {fmtDate(finished.format('YYYY-MM-DD'))}</div>}
        <div>Trạng thái: {isOverdue(t) ? `Quá hạn ${today.diff(due, 'day')} ngày` : STATUS_META[t.status].label}</div>
      </div>
    );
    return (
      <>
        {tail && tail.w > 0 && <div className={`absolute top-[11px] h-[18px] rounded-r ${tail.cls}`} style={{ left: tail.left, width: tail.w }} />}
        <Tooltip title={tip} mouseEnterDelay={0.15}>
          <button onClick={() => onOpen(t.id)}
            className={`absolute top-[9px] flex h-[22px] items-center [overflow:clip] rounded-md px-2 text-left text-[11px] font-medium text-white shadow-sm transition hover:brightness-110 ${!t.dueAt ? 'border border-dashed border-white/70' : ''}`}
            style={{ left, width: w, background: color, opacity: t.status === 'cancelled' ? 0.4 : 1 }}>
            {w > 70 && <span className="sticky whitespace-nowrap" style={{ left: LEFT + 8 }}>{t.name}</span>}
          </button>
        </Tooltip>
        {w <= 70 && <span className="pointer-events-none absolute top-[12px] whitespace-nowrap text-[11px] text-slate-500" style={{ left: left + w + (tail?.w ?? 0) + 6 }}>{t.name}</span>}
      </>
    );
  };

  const todayX = x(today) + dw / 2;

  return (
    <div className="rounded-xl border border-slate-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-3 py-2 text-xs text-slate-500 dark:border-gray-700">
        <Segmented size="small" value={zoom} onChange={v => setZoom(v as Zoom)} options={[{ value: 'day', label: 'Ngày' }, { value: 'week', label: 'Tuần' }, { value: 'month', label: 'Tháng' }]} />
        <button onClick={scrollToToday} className="flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 font-medium text-slate-600 hover:bg-slate-50 dark:border-gray-600 dark:text-gray-300"><LocateFixed size={13} /> Hôm nay</button>
        <span className="ml-auto flex flex-wrap items-center gap-3">
          {(['todo', 'doing', 'review', 'done'] as const).map(s => (
            <span key={s} className="flex items-center gap-1.5"><span className="h-2.5 w-4 rounded-sm" style={{ background: STATUS_META[s].color }} />{STATUS_META[s].label}</span>
          ))}
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-4 rounded-sm bg-red-400" />Trễ hạn</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-4 rounded-sm bg-amber-400" />Xong muộn</span>
        </span>
      </div>

      {rows.length === 0 ? <div className="py-16 text-center text-sm text-slate-400">Không có công việc</div> : (
        <div ref={scrollRef} className="relative max-h-[calc(100vh-300px)] min-h-[360px] overflow-auto">
          <div style={{ width: LEFT + width }} className="relative">
            {/* Header */}
            <div className="sticky top-0 z-20 flex border-b border-slate-200 bg-white dark:border-gray-700 dark:bg-gray-800">
              <div className="sticky left-0 z-30 flex shrink-0 items-end border-r border-slate-200 bg-white px-3 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:border-gray-700 dark:bg-gray-800" style={{ width: LEFT, height: zoom === 'month' ? 28 : 56 }}>
                Công việc
              </div>
              <div className="relative" style={{ width, height: zoom === 'month' ? 28 : 56 }}>
                {months.map(m => (
                  <div key={m.label} className="absolute top-0 flex h-7 items-center whitespace-nowrap [overflow:clip] border-l border-slate-200 px-2 text-xs font-semibold text-slate-600 dark:border-gray-700 dark:text-gray-300" style={{ left: m.left, width: m.w }}><span className="sticky" style={{ left: LEFT + 8 }}>{m.label}</span></div>
                ))}
                {ticks.map(tk => (
                  <div key={tk.key} className={`absolute top-7 flex h-7 flex-col items-center justify-center border-l border-slate-100 text-[11px] leading-none dark:border-gray-700 ${tk.isToday ? 'rounded bg-red-500 font-bold text-white' : tk.weekend ? 'text-slate-400' : 'text-slate-600 dark:text-gray-300'}`}
                    style={{ left: tk.left, width: tk.w }}>{tk.label}</div>
                ))}
              </div>
            </div>

            {/* Nền: cuối tuần + vạch hôm nay */}
            <div className="pointer-events-none absolute bottom-0 z-0" style={{ left: LEFT, top: zoom === 'month' ? 28 : 56, width }}>
              {zoom === 'day' && ticks.filter(tk => tk.weekend).map(tk => <div key={tk.key} className="absolute inset-y-0 bg-slate-50 dark:bg-gray-900/40" style={{ left: tk.left, width: tk.w }} />)}
              {zoom !== 'day' && months.map(m => <div key={m.label} className="absolute inset-y-0 border-l border-slate-100 dark:border-gray-700" style={{ left: m.left }} />)}
              <div className="absolute inset-y-0 w-px bg-red-500" style={{ left: todayX }} />
            </div>

            {rows.map(r => (r.kind === 'group' ? (
              <div key={`g-${r.id}`} className="relative z-10 flex border-b border-slate-100 bg-slate-50/80 dark:border-gray-700 dark:bg-gray-900/60" style={{ height: 34 }}>
                <button onClick={() => toggle(r.id)} className="sticky left-0 z-10 flex shrink-0 items-center gap-1.5 border-r border-slate-200 bg-slate-50 px-2 text-left text-[13px] font-semibold text-slate-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200" style={{ width: LEFT }}>
                  {collapsed.has(r.id) ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                  <span className="truncate">{r.name}</span><span className="font-normal text-slate-400">({r.count})</span>
                </button>
                <div className="relative" style={{ width }}>
                  <div className="absolute top-[15px] h-1 rounded bg-slate-300 dark:bg-gray-600" style={{ left: x(r.start), width: Math.max(dw, x(r.end) - x(r.start) + dw) }} />
                </div>
              </div>
            ) : (
              <div key={r.t.id} className="group relative z-10 flex border-b border-slate-100 hover:bg-sky-50/40 dark:border-gray-700 dark:hover:bg-gray-700/30" style={{ height: ROW }}>
                <button onClick={() => onOpen(r.t.id)} className="sticky left-0 z-10 flex shrink-0 items-center gap-2 border-r border-slate-200 bg-white pl-7 pr-3 text-left group-hover:bg-sky-50 dark:border-gray-700 dark:bg-gray-800" style={{ width: LEFT }}>
                  <Avatar id={r.t.assigneeId} size={22} />
                  <span className="min-w-0 flex-1">
                    <span className={`block truncate text-[13px] ${isClosed(r.t) ? 'text-slate-400 line-through' : 'font-medium text-slate-800 dark:text-gray-100'}`}>{r.t.name}</span>
                    <span className="block truncate text-[11px] text-slate-400">{r.t.code} · hạn {r.t.dueAt ? dayjs(r.t.dueAt).format('DD/MM') : '—'}</span>
                  </span>
                  {isOverdue(r.t) && <StatusTag t={r.t} />}
                </button>
                <div className="relative" style={{ width }}>{bar(r.t)}</div>
              </div>
            )))}
          </div>
        </div>
      )}
      {tasks.length > MAX_TASKS && <div className="border-t border-slate-100 px-3 py-2 text-xs text-slate-500">Đang hiện {MAX_TASKS}/{tasks.length} việc — dùng bộ lọc để thu hẹp.</div>}
    </div>
  );
}
